import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../lib/utils.js';

const inlineErrorMessageVariants = cva('rounded-lg border px-4 py-3 text-sm', {
  variants: {
    tone: {
      destructive: 'border-destructive/40 bg-destructive/10 text-destructive',
      warning: 'border-warning/40 bg-warning/10 text-warning',
    },
  },
  defaultVariants: {
    tone: 'destructive',
  },
});

export interface InlineErrorMessageProps extends VariantProps<typeof inlineErrorMessageVariants> {
  children: React.ReactNode;
  testId?: string;
  className?: string;
  /** Optional leading icon, e.g. `AlertOctagon`/`AlertTriangle`. */
  icon?: LucideIcon;
  /** Optional trailing action, e.g. a retry/resolve button. */
  action?: React.ReactNode;
}

/**
 * Small inline banner for form/upload validation feedback and severity
 * warnings. Extracted from three near-identical ad hoc copies across the app
 * (see CU-86cb1npz2) so future styling changes land in one place. `tone`
 * (default `'destructive'`) and the optional `icon` slot let callers such as
 * `UnmatchedMappingWarning` and the availability-import rematch-failed
 * banner render through this shared shape instead of hand-rolling their own
 * markup (CU-86cbafj4j).
 */
function InlineErrorMessage({
  children,
  testId,
  className,
  tone,
  icon: Icon,
  action,
}: InlineErrorMessageProps) {
  return (
    <div className={cn(inlineErrorMessageVariants({ tone }), className)} data-testid={testId}>
      {Icon || action ? (
        <span className="flex flex-wrap items-start gap-2">
          {Icon ? <Icon className="h-3.5 w-3.5 shrink-0 mt-0.5" /> : null}
          <span className="flex-1">{children}</span>
          {action}
        </span>
      ) : (
        children
      )}
    </div>
  );
}

export { InlineErrorMessage };
